import type { ArColumnDefinition } from '#types/System.Artifacts.types';
import { UIElementType, type CompositeFieldValue, type FieldType } from '#types/System.types';
import { getCompositeFieldType } from '@/components/CompositeField/compositeFieldTypeCache';
import { formatFieldValueParts } from '@/components/CompositeField/formatPreviewValue';
import { formattedCellValueToString } from '@/components/CompositeField/formattedCellValue';
import type { FormattedCellValue } from '@/components/CompositeField/formattedCellValue';

async function buildCompositeValueFormatter(
  columns: readonly ArColumnDefinition[],
  fieldTypes?: Record<string, FieldType>,
): Promise<(columnTypeId: string, cellValue: unknown) => string | number | FormattedCellValue | undefined> {
  const compositeColumnIds = columns
    .filter((col) => col.uiElementType === UIElementType.Field && fieldTypes?.[col.typeId]?.fieldDataTypeId === 'composite')
    .map((col) => col.typeId);

  const sellPriceColumnIds = new Set(
    columns
      .filter((col) => col.uiElementType === UIElementType.Field && fieldTypes?.[col.typeId]?.fieldDataTypeId === 'sellPriceUI')
      .map((col) => col.typeId)
  );

  const compositeFieldOrder = new Map<string, string[] | null>();
  await Promise.all(
    compositeColumnIds.map(async (typeId) => {
      const cft = await getCompositeFieldType(typeId);
      if (cft?.previewFieldIds?.length) {
        compositeFieldOrder.set(typeId, cft.previewFieldIds);
      } else if (cft) {
        compositeFieldOrder.set(typeId, null);
      }
    })
  );

  return (columnTypeId: string, cellValue: unknown): string | number | FormattedCellValue | undefined => {
    if (sellPriceColumnIds.has(columnTypeId)) {
      if (!cellValue || typeof cellValue !== 'object') return undefined;
      const fields = (cellValue as CompositeFieldValue).fields;
      const sellPrice = fields?.sellPrice?.value;
      if (typeof sellPrice === 'number') return sellPrice;
      return undefined;
    }

    const fieldOrder = compositeFieldOrder.get(columnTypeId);
    if (fieldOrder === undefined) return undefined;
    if (!cellValue || typeof cellValue !== 'object') return undefined;

    const composite = cellValue as CompositeFieldValue;
    const fields = composite.fields;
    if (!fields) return undefined;

    const ids = fieldOrder ?? Object.keys(fields).sort();
    const displayParts: string[] = [];
    let firstTrackingUrl: string | undefined;
    for (const id of ids) {
      const val = fields[id]?.value;
      if (val == null || val === '') continue;
      const part = formatFieldValueParts(val, fieldTypes?.[id]);
      const formattedValue = part.formatted;
      if (typeof formattedValue === 'object') {
        if (!firstTrackingUrl) firstTrackingUrl = formattedValue.url;
        displayParts.push(`${part.prefix ?? ''}${formattedValue.display}${part.suffix ?? ''}`);
      } else {
        displayParts.push(`${part.prefix ?? ''}${formattedCellValueToString(formattedValue)}${part.suffix ?? ''}`);
      }
    }
    if (displayParts.length === 0) return undefined;
    const display = displayParts.join(', ');
    if (!firstTrackingUrl) return display;
    return { display, url: firstTrackingUrl };
  };
}

export { buildCompositeValueFormatter };
